import { AspectRatio, ResourceType } from '@packages/global';
import type { XYPosition } from '@xyflow/react';

import type {
  BuildClipCanvasGraphOptions,
  ClipCanvasEdge,
  ClipCanvasGraph,
  ClipCanvasMediaTarget,
  ClipCanvasMediaType,
  ClipCanvasNode,
  ClipResourceGroupType,
  ClipImageTarget,
} from './types';

const COLUMN_X = {
  clip: 0,
  applied: 620,
  appliedSecondary: 1080,
  resources: 1580,
  resourcesSecondary: 2480,
  generators: 3220,
};

const LEGACY_COLUMN_X = {
  applied: 540,
  resources: 980,
};
const PREVIOUS_RESOURCE_COLUMN_X = 1120;
const LEGACY_ROW_GAP = 240;
const LEGACY_RESOURCE_COLUMN_GAP = 720;
const RESOURCE_COLUMN_GAP = 820;
const APPLIED_NODE_WIDTH = 360;
const RESOURCE_NODE_WIDTH = 720;
const NODE_GAP_Y = 80;

const APPLIED_LABELS: Record<ClipCanvasMediaTarget, string> = {
  start: '开始帧',
  end: '结束帧',
  storyboard: '分镜图',
  audio: '配音',
  effects: '特效',
  video: '当前视频',
};

const RESOURCE_GROUP_LABELS: Record<ClipResourceGroupType, string> = {
  image: '画面候选',
  audio: '配音候选',
  video: '视频候选',
};

export const CANVAS_IMAGE_RESOURCE_TYPES = [
  ResourceType.START_IMAGE,
  ResourceType.END_IMAGE,
  ResourceType.STORYBOARD_IMAGE,
] as const;

export const CANVAS_RESOURCE_TYPES = [
  ...CANVAS_IMAGE_RESOURCE_TYPES,
  ResourceType.VIDEO,
  ResourceType.SCRIPT_AUDIO,
  ResourceType.SOUND_EFFECT_AUDIO,
  ResourceType.LIP_SYNC_VIDEO,
  ResourceType.AUDIO_EDIT,
] as const;

export function isCanvasImageResourceType(resourceType: ResourceType): boolean {
  return CANVAS_IMAGE_RESOURCE_TYPES.includes(resourceType as typeof CANVAS_IMAGE_RESOURCE_TYPES[number]);
}

export function isCanvasResourceType(resourceType: ResourceType): boolean {
  return CANVAS_RESOURCE_TYPES.includes(resourceType as typeof CANVAS_RESOURCE_TYPES[number]);
}

export function getImageTargetFromResourceType(resourceType: ResourceType): ClipImageTarget | undefined {
  if (resourceType === ResourceType.START_IMAGE) return 'start';
  if (resourceType === ResourceType.END_IMAGE) return 'end';
  if (resourceType === ResourceType.STORYBOARD_IMAGE) return 'storyboard';
  return undefined;
}

function getResourceGroupType(resourceType: ResourceType): ClipResourceGroupType | undefined {
  if (isCanvasImageResourceType(resourceType)) return 'image';
  if (resourceType === ResourceType.SCRIPT_AUDIO || resourceType === ResourceType.SOUND_EFFECT_AUDIO || resourceType === ResourceType.AUDIO_EDIT) return 'audio';
  if (resourceType === ResourceType.VIDEO || resourceType === ResourceType.LIP_SYNC_VIDEO) return 'video';
  return undefined;
}

function isLegacyAutoPosition(
  nodeId: string,
  position: XYPosition,
  options?: BuildClipCanvasGraphOptions,
): boolean {
  const appliedOrder = ['start', 'end', 'storyboard', 'audio', 'effects', 'video'];
  const appliedMatch = nodeId.match(/^applied:[^:]+:(.+)$/);
  if (appliedMatch) {
    const index = appliedOrder.indexOf(appliedMatch[1]);
    const currentOneColumnPosition = options && index >= 0
      ? {
        x: COLUMN_X.applied,
        y: getAppliedNodeY(index, options),
      }
      : null;
    return index >= 0
      && (
        (position.x === LEGACY_COLUMN_X.applied && position.y === index * LEGACY_ROW_GAP)
        || (currentOneColumnPosition !== null && position.x === currentOneColumnPosition.x && position.y === currentOneColumnPosition.y)
      );
  }

  const resourceOrder = ['image', 'audio', 'video'];
  const resourceMatch = nodeId.match(/^resource-group:[^:]+:(.+)$/);
  if (resourceMatch) {
    const index = resourceOrder.indexOf(resourceMatch[1]);
    const previousDefaultPosition = options && index >= 0
      ? getPreviousResourceGroupPosition(resourceMatch[1] as ClipResourceGroupType, index, options)
      : null;
    return index >= 0
      && (
        (position.x === LEGACY_COLUMN_X.resources + index * LEGACY_RESOURCE_COLUMN_GAP && position.y === 0)
        || (
          previousDefaultPosition !== null
          && position.x === previousDefaultPosition.x
          && position.y === previousDefaultPosition.y
        )
      );
  }

  return false;
}

function getPreservedPosition(
  nodeId: string,
  previousNodes?: ClipCanvasNode[],
  options?: BuildClipCanvasGraphOptions,
): XYPosition | undefined {
  const position = previousNodes?.find((node) => node.id === nodeId)?.position;
  if (!position) return undefined;
  return isLegacyAutoPosition(nodeId, position, options) ? undefined : position;
}

function withPosition(
  nodeId: string,
  fallback: XYPosition,
  previousNodes?: ClipCanvasNode[],
  options?: BuildClipCanvasGraphOptions,
): XYPosition {
  return getPreservedPosition(nodeId, previousNodes, options) ?? fallback;
}

function getAspectRatioNumber(aspectRatio?: AspectRatio): number {
  switch (aspectRatio) {
    case AspectRatio.SQUARE:
      return 1;
    case AspectRatio.PORTRAIT:
      return 9 / 16;
    case AspectRatio.STANDARD:
      return 4 / 3;
    case AspectRatio.PORTRAIT_STANDARD:
      return 3 / 4;
    case AspectRatio.LANDSCAPE:
    default:
      return 16 / 9;
  }
}

function getMediaPreviewHeight(width: number, aspectRatio?: AspectRatio): number {
  const ratio = getAspectRatioNumber(aspectRatio);
  return width / Math.max(ratio, 0.1);
}

function estimateAppliedNodeHeight(mediaType: ClipCanvasMediaType, options: BuildClipCanvasGraphOptions, hasSummary?: boolean): number {
  const header = 58;
  const footer = hasSummary ? 76 : 0;
  if (mediaType === 'audio' || mediaType === 'effects') {
    return header + 110 + footer;
  }
  return header + getMediaPreviewHeight(APPLIED_NODE_WIDTH - 20, options.mediaAspectRatio) + footer;
}

function estimateResourceGroupHeight(groupType: ClipResourceGroupType, count: number, options: BuildClipCanvasGraphOptions): number {
  const header = 44;
  if (count === 0) return header + 120;

  const rows = Math.min(2, Math.ceil(count / 2));
  const itemWidth = (RESOURCE_NODE_WIDTH - 20 - 12) / 2;
  const mediaHeight = groupType === 'audio' ? 88 : getMediaPreviewHeight(itemWidth, options.mediaAspectRatio);
  const itemHeight = 56 + mediaHeight + 78;
  return header + rows * itemHeight + Math.max(0, rows - 1) * 12 + 20;
}

function getResourceGroups(resources: BuildClipCanvasGraphOptions['resources']): Array<{
  type: ClipResourceGroupType;
  resources: BuildClipCanvasGraphOptions['resources'];
}> {
  const resourceGroups: Array<{ type: ClipResourceGroupType; resources: BuildClipCanvasGraphOptions['resources'] }> = [
    { type: 'image', resources: [] },
    { type: 'audio', resources: [] },
    { type: 'video', resources: [] },
  ];

  resources.forEach((resource) => {
    const groupType = getResourceGroupType(resource.resourceType);
    if (!groupType) return;
    resourceGroups.find((group) => group.type === groupType)?.resources.push(resource);
  });

  return resourceGroups;
}

function getPreviousResourceGroupPosition(
  _groupType: ClipResourceGroupType,
  index: number,
  options: BuildClipCanvasGraphOptions,
): XYPosition {
  const resourceGroups = getResourceGroups(options.resources);
  const previousGroupsHeight = resourceGroups
    .slice(0, index)
    .reduce(
      (sum, item) => sum + estimateResourceGroupHeight(item.type, item.resources.length, options) + NODE_GAP_Y,
      0,
    );

  return {
    x: PREVIOUS_RESOURCE_COLUMN_X + index * RESOURCE_COLUMN_GAP,
    y: previousGroupsHeight,
  };
}

function getResourceGroupPosition(
  groupType: ClipResourceGroupType,
  resourceGroups: Array<{ type: ClipResourceGroupType; resources: BuildClipCanvasGraphOptions['resources'] }>,
  options: BuildClipCanvasGraphOptions,
): XYPosition {
  if (groupType === 'image') {
    return {
      x: COLUMN_X.resources,
      y: 0,
    };
  }

  const audioGroup = resourceGroups.find((group) => group.type === 'audio');
  const audioHeight = estimateResourceGroupHeight('audio', audioGroup?.resources.length ?? 0, options);
  return {
    x: COLUMN_X.resourcesSecondary,
    y: groupType === 'audio' ? 0 : audioHeight + NODE_GAP_Y,
  };
}

function buildAppliedNode(
  id: string,
  label: string,
  url: string | undefined,
  mediaType: ClipCanvasMediaType,
  target: ClipCanvasMediaTarget,
  options: BuildClipCanvasGraphOptions,
  summary?: string,
): ClipCanvasNode {
  return {
    id,
    type: 'appliedMedia',
    position: withPosition(
      id,
      getAppliedNodePosition(target, options),
      options.previousNodes,
      options,
    ),
    data: {
      kind: 'applied-media',
      id,
      label,
      mediaType,
      url,
      target,
      summary,
      actions: options.actions,
    },
  };
}

function getAppliedNodePosition(target: ClipCanvasMediaTarget, options: BuildClipCanvasGraphOptions): XYPosition {
  const imageTargets: ClipCanvasMediaTarget[] = ['start', 'end', 'storyboard'];
  const secondaryTargets: ClipCanvasMediaTarget[] = ['audio', 'effects', 'video'];
  const imageIndex = imageTargets.indexOf(target);
  if (imageIndex >= 0) {
    return {
      x: COLUMN_X.applied,
      y: getAppliedNodeY(imageIndex, options),
    };
  }

  const secondaryIndex = secondaryTargets.indexOf(target);
  return {
    x: COLUMN_X.appliedSecondary,
    y: getAppliedSecondaryNodeY(Math.max(secondaryIndex, 0), options),
  };
}

function getAppliedNodeY(targetIndex: number, options: BuildClipCanvasGraphOptions): number {
  const mediaStack: Array<{ mediaType: ClipCanvasMediaType; hasSummary?: boolean }> = [
    { mediaType: 'image' },
    { mediaType: 'image' },
    { mediaType: 'image' },
  ];

  return mediaStack.slice(0, targetIndex).reduce(
    (sum, item) => sum + estimateAppliedNodeHeight(item.mediaType, options, item.hasSummary) + NODE_GAP_Y,
    0,
  );
}

function getAppliedSecondaryNodeY(targetIndex: number, options: BuildClipCanvasGraphOptions): number {
  const mediaStack: Array<{ mediaType: ClipCanvasMediaType; hasSummary?: boolean }> = [
    { mediaType: 'audio', hasSummary: Boolean(options.videoClip.script) },
    { mediaType: 'effects', hasSummary: Boolean(options.videoClip.effectItems) },
    { mediaType: 'video' },
  ];

  return mediaStack.slice(0, targetIndex).reduce(
    (sum, item) => sum + estimateAppliedNodeHeight(item.mediaType, options, item.hasSummary) + NODE_GAP_Y,
    0,
  );
}

export function buildClipCanvasGraph(options: BuildClipCanvasGraphOptions): ClipCanvasGraph {
  const { videoClip, resources, actions, previousNodes } = options;
  const nodes: ClipCanvasNode[] = [
    {
      id: `clip:${videoClip.id}`,
      type: 'clipContext',
      position: withPosition(`clip:${videoClip.id}`, { x: COLUMN_X.clip, y: 0 }, previousNodes),
      data: {
        kind: 'clip-context',
        videoClip,
        actions,
      },
    },
  ];

  const edges: ClipCanvasEdge[] = [];
  const appliedMedia: Array<{
    id: string;
    label: string;
    url?: string;
    mediaType: ClipCanvasMediaType;
    target: ClipCanvasMediaTarget;
    summary?: string;
  }> = [
    { id: `applied:${videoClip.id}:start`, label: APPLIED_LABELS.start, url: videoClip.startImage, mediaType: 'image', target: 'start' },
    { id: `applied:${videoClip.id}:end`, label: APPLIED_LABELS.end, url: videoClip.endImage, mediaType: 'image', target: 'end' },
    { id: `applied:${videoClip.id}:storyboard`, label: APPLIED_LABELS.storyboard, url: videoClip.storyboardImage, mediaType: 'image', target: 'storyboard' },
    { id: `applied:${videoClip.id}:audio`, label: APPLIED_LABELS.audio, url: videoClip.scriptAudioUrl, mediaType: 'audio', target: 'audio', summary: videoClip.script || undefined },
    { id: `applied:${videoClip.id}:effects`, label: APPLIED_LABELS.effects, url: undefined, mediaType: 'effects', target: 'effects', summary: videoClip.effectItems ? '已配置特效' : undefined },
    { id: `applied:${videoClip.id}:video`, label: APPLIED_LABELS.video, url: videoClip.videoUrl, mediaType: 'video', target: 'video' },
  ];

  appliedMedia.forEach((media) => {
    nodes.push(
      buildAppliedNode(
        media.id,
        media.label,
        media.url,
        media.mediaType,
        media.target,
        options,
        media.summary,
      ),
    );
    edges.push({
      id: `edge:clip:${videoClip.id}:${media.id}`,
      source: `clip:${videoClip.id}`,
      target: media.id,
      animated: false,
    });
  });

  const resourceGroups = getResourceGroups(resources);

  resourceGroups.forEach((group) => {
    const nodeId = `resource-group:${videoClip.id}:${group.type}`;

    nodes.push({
      id: nodeId,
      type: 'candidateResource',
      position: withPosition(
        nodeId,
        getResourceGroupPosition(group.type, resourceGroups, options),
        previousNodes,
        options,
      ),
      data: {
        kind: 'candidate-resource',
        label: RESOURCE_GROUP_LABELS[group.type],
        groupType: group.type,
        resources: group.resources,
        actions,
      },
    });

    edges.push({
      id: `edge:clip:${videoClip.id}:${nodeId}`,
      source: `clip:${videoClip.id}`,
      target: nodeId,
      animated: group.resources.some((resource) => resource.status !== 'completed'),
    });
  });

  return { nodes, edges };
}
